import type { components } from "@sagot/api-client";
import { GLOSSARY_SYNONYMS_MAX, GLOSSARY_TERM_MAX, REPLY_MAX_WORDS_MAX, REPLY_MAX_WORDS_MIN } from "@sagot/constants";

type Schemas = components["schemas"];

export type GlossaryTerm = Schemas["GlossaryTermOut"];
export type GlossaryTermCreate = Schemas["GlossaryTermCreate"];
export type GlossaryTermPatch = Schemas["GlossaryTermPatch"];
export type GlossaryLocale = GlossaryTerm["locale"];
export type GlossaryScope = "all" | "agent";

/** The limits of `@sagot/constants` (mirror of `core/constants/glossary.py`) under the names this tab reads. */
export const GLOSSARY_LIMITS = { termMax: GLOSSARY_TERM_MAX, synonymsMax: GLOSSARY_SYNONYMS_MAX, replyMin: REPLY_MAX_WORDS_MIN, replyMax: REPLY_MAX_WORDS_MAX } as const;
export const GLOSSARY_LOCALES: readonly GlossaryLocale[] = ["any", "en", "fil", "taglish"];
export const SEARCH_DEBOUNCE_MS = 300;

/** One synonym row of the dialog; `key` keeps React rows stable while the text is edited. */
export interface SynonymDraft {
  key: number;
  text: string;
  locale: GlossaryLocale;
}

export interface TermDraft {
  term: string;
  locale: GlossaryLocale;
  synonyms: SynonymDraft[];
  protected: boolean;
  escalation: boolean;
  scope: GlossaryScope;
}

let nextKey = 0;
export function newSynonym(text = "", locale: GlossaryLocale = "any"): SynonymDraft {
  nextKey += 1;
  return { key: nextKey, text, locale };
}

export function emptyDraft(): TermDraft {
  return { term: "", locale: "any", synonyms: [], protected: false, escalation: false, scope: "all" };
}

export function draftOf(term: GlossaryTerm): TermDraft {
  return {
    term: term.term,
    locale: term.locale,
    synonyms: term.synonyms.map((s) => newSynonym(s.text, s.locale)),
    protected: term.protected,
    escalation: term.escalation,
    scope: term.agent_id ? "agent" : "all",
  };
}

/** The synonyms to send: trimmed, blanks dropped. */
export function synonymsOf(draft: TermDraft): GlossaryTermCreate["synonyms"] {
  return draft.synonyms
    .map((s) => ({ text: s.text.trim(), locale: s.locale }))
    .filter((s) => s.text.length > 0);
}

export function toCreate(draft: TermDraft, agentId: string): GlossaryTermCreate {
  return {
    agent_id: draft.scope === "agent" ? agentId : null,
    term: draft.term.trim(),
    synonyms: synonymsOf(draft),
    locale: draft.locale,
    protected: draft.protected,
    escalation: draft.escalation,
  };
}

export function toPatch(draft: TermDraft): GlossaryTermPatch {
  return {
    term: draft.term.trim(),
    synonyms: synonymsOf(draft),
    locale: draft.locale,
    protected: draft.protected,
    escalation: draft.escalation,
  };
}

export type TermProblem = "required" | "tooLong" | null;

export function termProblem(draft: TermDraft): TermProblem {
  const length = draft.term.trim().length;
  if (length === 0) return "required";
  return length > GLOSSARY_LIMITS.termMax ? "tooLong" : null;
}

/** `null` = no cap. A cap is a whole number inside the allowed range. */
export function capProblem(value: string): boolean {
  const words = Number(value);
  return !Number.isInteger(words) || words < GLOSSARY_LIMITS.replyMin || words > GLOSSARY_LIMITS.replyMax;
}

export function replyCapOf(behavior: Record<string, unknown> | undefined): number | null {
  const cap = behavior?.reply_max_words;
  return typeof cap === "number" && Number.isInteger(cap) ? cap : null;
}
